import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { SupabaseClientService } from './db/supabase-client';
import {
  Workflow,
  WorkflowInsert,
  WorkflowUpdate
} from '@models/workflow.types';

export interface TaskChartData {
  date: string;
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {
  constructor(
    private supabase: SupabaseClientService
  ) {}

  /**
   * Get all workflows ordered by updated_at (most recent first)
   * Optimized: limits results to prevent loading too much data
   * Note: Includes prompt field which may be large - consider pagination for many workflows
   */
  getWorkflows(): Observable<Workflow[]> {
    return from(
      (async () => {
        const { data, error } = await this.supabase.client
          .from('workflows')
          .select('*')
          .order('updated_at', { ascending: false })
          .limit(500);

        if (error) throw error;
        return (data as Workflow[]) || [];
      })()
    ).pipe(
      catchError(error => {
        console.error('Error loading workflows:', error);
        return throwError(() => error);
      })
    );
  }

  createWorkflow(workflowData: WorkflowInsert): Observable<Workflow[]> {
    return this.supabase.insert<Workflow>('workflows', workflowData);
  }

  updateWorkflow(id: string, updates: WorkflowUpdate): Observable<Workflow[]> {
    return this.supabase.update<Workflow>('workflows', updates, { id });
  }

  /**
   * Delete workflow
   */
  deleteWorkflow(id: string): Observable<void> {
    return this.supabase.delete('workflows', { id });
  }
}
